import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "../utils/toast";
import Swal from "../utils/swal";
import WorkWithUs from "../components/sections/WorkWithUs";
import { useEffect, useState } from "react";

const ProfileRow = ({
  label,
  name,
  value,
  editMode,
  onChange,
  type = "text",
  maxLength,
  inputMode,
  children,
}) => (
  <>
    <div className="profile_rightList">
      <span>{label}</span>
      {editMode ? (
        <div className="profile_editInput">
          {children || (
            <input
              name={name}
              type={type}
              value={value}
              onChange={onChange}
              placeholder={label}
              maxLength={maxLength}
              inputMode={inputMode}
            />
          )}
        </div>
      ) : (
        <p>{value || "—"}</p>
      )}
    </div>
    <hr />
  </>
);

const applyCustomer = (c, setCustomer, setForm) => {
  if (!c) return;
  setCustomer(c);
  setForm({
    first_name: c.first_name || "",
    last_name: c.last_name || "",
    email: c.email || "",
    phone: c.phone || "",
  });
};

const Profile = () => {
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [authError, setAuthError] = useState("");

  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });

  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("user_token")}`,
  });

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("user_token");
      if (!token) {
        setAuthError("login");
        setLoading(false);
        return;
      }

      // Show cached user immediately so header/page stay in sync while API loads.
      let hasCachedCustomer = false;
      try {
        const raw =
          localStorage.getItem("user_data") || localStorage.getItem("user");
        if (raw) {
          const cached = JSON.parse(raw);
          if (cached) {
            applyCustomer(cached, setCustomer, setForm);
            hasCachedCustomer = true;
          }
        }
      } catch {
        // ignore bad cache
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_APP_DOMAIN}/api/customer/profile`,
          { headers: getHeaders() },
        );
        if (response.data.status && response.data.customer) {
          const c = response.data.customer;
          applyCustomer(c, setCustomer, setForm);
          localStorage.setItem("user_data", JSON.stringify(c));
          localStorage.setItem("user", JSON.stringify(c));
          window.dispatchEvent(new Event("loginStateChange"));
          setAuthError("");
        } else if (!hasCachedCustomer) {
          setAuthError("failed");
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem("user_token");
          localStorage.removeItem("user_data");
          localStorage.removeItem("user");
          window.dispatchEvent(new Event("loginStateChange"));
          setCustomer(null);
          setAuthError("login");
        } else if (!hasCachedCustomer) {
          setAuthError("failed");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      setForm({ ...form, phone: value.replace(/\D/g, "").slice(0, 10) });
      return;
    }
    setForm({ ...form, [name]: value });
  };

  const handleSave = async () => {
    const phone = form.phone.replace(/\D/g, "");
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("Please enter first name and last name");
      return;
    }
    if (!/^[a-zA-Z\s]+$/.test(form.first_name.trim())) {
      toast.error("First name may only contain letters.");
      return;
    }
    if (!/^[a-zA-Z\s]+$/.test(form.last_name.trim())) {
      toast.error("Last name may only contain letters.");
      return;
    }
    if (!/^\d{10}$/.test(phone)) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/update-profile`,
        { ...form, phone },
        { headers: getHeaders() },
      );
      if (response.data.status) {
        const updatedCustomer = response.data.customer;
        setCustomer(updatedCustomer);
        setEditMode(false);
        localStorage.setItem("user_data", JSON.stringify(updatedCustomer));
        localStorage.setItem("user", JSON.stringify(updatedCustomer));
        window.dispatchEvent(new Event("loginStateChange"));
        toast.success("Profile updated successfully!");
      } else {
        toast.error("Failed to update profile.");
      }
    } catch (error) {
      const msg = error?.response?.data?.message || "Failed to update profile.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (customer) {
      setForm({
        first_name: customer.first_name || "",
        last_name: customer.last_name || "",
        email: customer.email || "",
        phone: customer.phone || "",
      });
    }
    setEditMode(false);
  };

  const handleLogout = () => {
    Swal.fire({
      title: "Logout?",
      text: "Are you sure you want to log out?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, logout!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.removeItem("user_token");
        localStorage.removeItem("user_data");
        toast.success("Logged out successfully");
        navigate("/");
        window.location.reload();
      }
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "300px" }}
      >
        <div className="spinner-border text-warning" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!customer) {
    if (authError === "failed") {
      return (
        <div className="text-center p-5">
          <h4>Unable to load profile</h4>
          <p className="text-muted mb-3">Please try again in a moment.</p>
          <button
            type="button"
            className="btn btn-warning"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      );
    }

    return (
      <div className="text-center p-5">
        <h4>Please login to view profile</h4>
        <Link to="/login" className="btn btn-warning mt-3">
          Log In
        </Link>
      </div>
    );
  }

  const fullName = `${customer.first_name} ${customer.last_name}`;
  const initial = customer.first_name
    ? customer.first_name.charAt(0).toUpperCase()
    : "";

  return (
    <>
      <section className="profile_wrap section_pb section_pt">
        <div className="container">
          <div className="row">
            <div className="col-lg-5 col-md-5 col-sm-12 col-12">
              <div className="profile_leftMain">
                <div className="profile_col">
                  <div className="profile_name">
                    <p>{initial}</p>
                    <h3>{fullName}</h3>
                    <span>{customer.phone}</span>
                  </div>
                  <div className="profile_seeTrip">
                    <Link to="/bookings">See All Bookings</Link>
                  </div>
                  <div className="user_sinc">
                    <p>User Since {formatDate(customer.created_at)}</p>
                  </div>
                </div>
                <div className="profile_logout">
                  <Link to="#!" className="logout" onClick={handleLogout}>
                    Log Out
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-lg-7 col-md-7 col-sm-12 col-12">
              <div className="profile_rightMain">
                <div className="profile_rightTItle">
                  <h3>Profile Details</h3>
                  {!editMode ? (
                    <button
                      className="profile_editBtn_trigger"
                      onClick={() => setEditMode(true)}
                    >
                      Edit Profile
                    </button>
                  ) : (
                    <div className="profile_editBtns">
                      <button
                        className="btn_save"
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? "Saving..." : "Save"}
                      </button>
                      <button className="btn_cancel" onClick={handleCancel}>
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                <hr />

                <ProfileRow
                  label="First Name"
                  name="first_name"
                  value={form.first_name}
                  editMode={editMode}
                  onChange={handleChange}
                />
                <ProfileRow
                  label="Last Name"
                  name="last_name"
                  value={form.last_name}
                  editMode={editMode}
                  onChange={handleChange}
                />
                <ProfileRow
                  label="Phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  editMode={editMode}
                  onChange={handleChange}
                  maxLength={10}
                  inputMode="numeric"
                />
                {(customer?.email || editMode) && (
                  <ProfileRow
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    editMode={editMode}
                    onChange={handleChange}
                  />
                )}



              </div>
            </div>
          </div>
        </div>
      </section>
      <WorkWithUs />
    </>
  );
};

export default Profile;
